/**
 * ANJA saved items (wishlist) and recently viewed products, kept in this browser only (localStorage).
 * - [data-anja-wish="handle"] buttons toggle a product in the wishlist (event delegation, page-wide).
 * - [data-anja-wish-count] elements show the number of saved items.
 * - <anja-recent-track data-handle> records the current product as recently viewed.
 * - <anja-product-strip data-source="recent|wishlist"> fills itself with server-rendered cards.
 */
const KEYS = { wishlist: 'anja:wishlist', recent: 'anja:recent' };
const MAX = { wishlist: 60, recent: 16 };

/** @param {'wishlist'|'recent'} key @returns {string[]} */
function read(key) {
  try {
    const list = JSON.parse(localStorage.getItem(KEYS[key]) || '[]');
    return Array.isArray(list) ? list.filter((h) => typeof h === 'string') : [];
  } catch {
    return [];
  }
}

/** @param {'wishlist'|'recent'} key @param {string[]} list */
function write(key, list) {
  try {
    localStorage.setItem(KEYS[key], JSON.stringify(list.slice(0, MAX[key])));
  } catch {
    /* storage unavailable: features degrade silently */
  }
  document.dispatchEvent(new CustomEvent('anja-saved:change', { detail: { key } }));
}

function syncWishUi() {
  const saved = new Set(read('wishlist'));
  document.querySelectorAll('[data-anja-wish]').forEach((btn) => {
    btn.setAttribute('aria-pressed', String(saved.has(/** @type {HTMLElement} */ (btn).dataset.anjaWish || '')));
  });
  document.querySelectorAll('[data-anja-wish-count]').forEach((el) => {
    el.textContent = saved.size ? String(saved.size) : '';
    el.toggleAttribute('hidden', !saved.size);
  });
}

if (!window.__anjaSaved) {
  window.__anjaSaved = true;
  document.addEventListener('click', (event) => {
    const btn = /** @type {HTMLElement} */ (event.target).closest?.('[data-anja-wish]');
    if (!(btn instanceof HTMLElement)) return;
    event.preventDefault();
    const handle = btn.dataset.anjaWish || '';
    const list = read('wishlist');
    const index = list.indexOf(handle);
    if (index === -1) list.unshift(handle);
    else list.splice(index, 1);
    write('wishlist', list);
    const live = document.querySelector('[data-anja-saved-live]');
    if (live) live.textContent = index === -1 ? live.dataset.added || '' : live.dataset.removed || '';
  });
  document.addEventListener('anja-saved:change', syncWishUi);
  addEventListener('storage', syncWishUi);
  syncWishUi();
}

class AnjaRecentTrack extends HTMLElement {
  connectedCallback() {
    const handle = this.dataset.handle;
    if (!handle) return;
    write('recent', [handle, ...read('recent').filter((h) => h !== handle)]);
  }
}

class AnjaProductStrip extends HTMLElement {
  #loaded = '';

  connectedCallback() {
    this.render();
    if (this.dataset.source === 'wishlist') {
      document.addEventListener('anja-saved:change', this.#onChange);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('anja-saved:change', this.#onChange);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    if (/** @type {CustomEvent} */ (event).detail?.key === 'wishlist') this.render();
  };

  async render() {
    const source = this.dataset.source === 'wishlist' ? 'wishlist' : 'recent';
    const exclude = this.dataset.exclude || '';
    const limit = Number(this.dataset.limit) || 8;
    const handles = read(source)
      .filter((h) => h !== exclude)
      .slice(0, limit);
    const key = handles.join(',');
    if (key === this.#loaded) return;
    this.#loaded = key;

    const grid = this.querySelector('[data-anja-strip-grid]');
    const empty = this.querySelector('[data-anja-strip-empty]');
    if (!grid) return;
    if (!handles.length) {
      grid.replaceChildren();
      this.toggleAttribute('data-empty', true);
      if (empty) empty.hidden = false;
      return;
    }

    const root = this.dataset.root || '/';
    const cards = await Promise.all(
      handles.map(async (handle) => {
        try {
          const res = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=anja-card-render`);
          if (!res.ok) return null;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          return doc.querySelector('.anja-mini-card');
        } catch {
          return null;
        }
      })
    );
    if (key !== this.#loaded) return;
    const found = /** @type {Element[]} */ (cards.filter(Boolean));
    grid.replaceChildren(...found);
    this.toggleAttribute('data-empty', !found.length);
    if (empty) empty.hidden = found.length > 0;
    syncWishUi();
  }
}

if (!customElements.get('anja-recent-track')) customElements.define('anja-recent-track', AnjaRecentTrack);
if (!customElements.get('anja-product-strip')) customElements.define('anja-product-strip', AnjaProductStrip);
